import * as React from 'react'
import styled from 'styled-components'

import * as glyphs from '../../icons/glyphs'
import { EditButton } from '../button/EditButton'
import { LinkAsButton } from '../button/LinkAsButton'
import { Tag } from '../tag/Tag'
import { VideoLoader, type VideoLoaderProps } from '../VideoLoader'
import { Link } from '../Link'

import * as styles from '../../styles'
import { Tooltip } from '../Tooltip'

export type Media =
  | { type: 'image'; src: string }
  | {
      consentWall: VideoLoaderProps['consentWall']
      type: 'video'
      placeholder: string
      videoHTML: string
    }

export type NewsCardProps = {
  isEditMode?: boolean
  className?: string
  content?: string
  editingLink?: string
  link: { href: string; onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void }
  locked?: { subscriptions?: string[]; backers?: boolean; tip?: boolean; fans?: boolean }
  isVideoActive?: boolean
  media?: Media
  date: string | undefined
  onVideoMount?: () => void
  title: string
  translations: {
    editCTA?: string
    commentCount: string
    locked: string
    lockedIcon?: React.ReactElement
    freeDonation: string
    lockTooltip: string
    followCTA?: string
    readCTA?: string
    subscribeCTA?: string
  }
}

const NewsCardComponent = React.forwardRef<HTMLDivElement, NewsCardProps>(function NewsCardComponent(
  {
    className,
    isEditMode = false,
    content,
    editingLink,
    link,
    locked,
    isVideoActive = true,
    date,
    onVideoMount,
    title,
    translations,
    media,
  },
  ref,
): React.ReactElement {
  const state = getLockState()

  return (
    <styles.card.news.NewsCardWrapper className={className} ref={ref}>
      {isEditMode && editingLink && (
        <a href={editingLink}>
          <EditButton glyph={<glyphs.stroke.Edit size={18} />}>{translations.editCTA}</EditButton>
        </a>
      )}
      <Link href={link.href} onClick={link.onClick}>
        {media && (
          <styles.card.news.NewsCardMediaWrapper $isLocked={state.isLocked} $type={media.type}>
            {media.type === 'image' && <styles.card.news.NewsCardImage src={media.src} alt={title} loading="lazy" />}
            {media.type === 'video' && (
              <VideoLoader
                consentWall={media.consentWall}
                isActive={isVideoActive}
                onVideoMount={onVideoMount}
                isLocked={state.isLocked}
                placeholder={media.placeholder}
                videoHtml={media.videoHTML}
              />
            )}

            <styles.card.news.LockLabel asDesktop="L" $isLocked={state.isLocked} $type={media.type}>
              <glyphs.stroke.Lock size={21} />
              {translations.locked}
            </styles.card.news.LockLabel>
          </styles.card.news.NewsCardMediaWrapper>
        )}
        <styles.card.news.NewsCardContainer>
          <styles.card.news.NewsCardHeader>
            {date && <styles.card.news.NewsCardDate>{date}</styles.card.news.NewsCardDate>}
            {state.isLocked && (
              <Tag color="sand">
                <glyphs.stroke.Lock size={12} />
                {translations.locked}
                {translations.lockedIcon}
                {state.tooltip && (
                  <Tooltip open={false} fitToContent position="top">
                    <styles.copy.S>{translations.lockTooltip}</styles.copy.S>
                    <ul>
                      {state.tooltip.map((item) => {
                        return (
                          <li key={item}>
                            <styles.copy.S>{item}</styles.copy.S>
                          </li>
                        )
                      })}
                    </ul>
                  </Tooltip>
                )}
              </Tag>
            )}
          </styles.card.news.NewsCardHeader>
          <styles.card.news.NewsCardTitle asDesktop="S">{title}</styles.card.news.NewsCardTitle>
          {content && (
            <styles.card.news.NewsCardDescription
              dangerouslySetInnerHTML={{ __html: content }}
              $isLocked={state.isLocked}
            />
          )}
          <styles.card.news.NewsCardFooter>
            <styles.card.news.CommentsContainer>
              <glyphs.stroke.Bubble size={11} />
              <styles.card.news.CommentsCount>{translations.commentCount}</styles.card.news.CommentsCount>
            </styles.card.news.CommentsContainer>
            <styles.card.news.SubscriptionContainer>
              <styles.card.news.SubscriptionText>
                <SubscriptionCTA tinted>{getLockCTA()}</SubscriptionCTA>
              </styles.card.news.SubscriptionText>
              <glyphs.stroke.ArrowRight size={16} />
            </styles.card.news.SubscriptionContainer>
          </styles.card.news.NewsCardFooter>
        </styles.card.news.NewsCardContainer>
      </Link>
    </styles.card.news.NewsCardWrapper>
  )

  function getLockCTA(): string | undefined {
    if (state.isLocked) {
      return locked?.fans === true ? translations.followCTA : translations.subscribeCTA
    }

    return translations.readCTA
  }

  function getLockState(): { isLocked: false } | { isLocked: true; tooltip?: string[] } {
    if (locked === undefined) {
      return { isLocked: false }
    }

    if (locked.fans === true) {
      return { isLocked: true }
    }

    let isLocked = false
    const tooltip: string[] = []
    if (locked.tip === true) {
      isLocked = true
      tooltip.push(translations.freeDonation)
    }
    if (locked.subscriptions !== undefined && locked.subscriptions.length > 0) {
      isLocked = true
      tooltip.push(...locked.subscriptions)
    }
    if (locked.backers === true) {
      isLocked = true
    }

    return { isLocked, tooltip: tooltip.length === 0 ? undefined : tooltip }
  }
})

export const NewsCard = styled(NewsCardComponent)``

const SubscriptionCTA = styled(LinkAsButton)`
  padding: 0;
`
